import React, { useState, useEffect } from 'react'
import Modal, { ModalProps } from 'components/modal'
import { useProject } from 'hooks/useProject'
import { useApplicationStore } from 'hooks/application'
import { ITableViewWithColumns } from 'types'
import { IColumnTypes } from 'components/spreadsheet/types'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import Button from 'components/button'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import api from 'helpers/api'
import { HUB_BASE } from 'routes'
import { COLUMN_TYPE_NAMES, DATE_COLUMN_TYPES } from 'components/spreadsheet/constants/const'
import { detectLikelyDateFormat, detectLikelyDatetimeFormat } from 'components/spreadsheet/helpers/date'
import { getRowValue } from 'components/spreadsheet/helpers/functions'
import { summariseKindChange } from 'components/spreadsheet/helpers/columnKindChange'

const KindChangeWarning: React.FC<{ previousKind: IColumnTypes | undefined; newKind: IColumnTypes }> = ({
  previousKind,
  newKind
}) => {
  if (!previousKind) {
    return (
      <>
        Are you sure you want to change the kind of this column? There is a chance for{' '}
        <b className="text-red">data to be lost</b> in all of the cells in this column.{' '}
      </>
    )
  }

  const { outcome, detail } = summariseKindChange(previousKind, newKind)
  const change = (
    <>
      Changing this column from <b>{COLUMN_TYPE_NAMES[previousKind]}</b> to <b>{COLUMN_TYPE_NAMES[newKind]}</b>
    </>
  )

  if (outcome === 'cleared') {
    return (
      <>
        {change} will <b className="text-red">clear every value</b> in it. This cannot be undone.{' '}
      </>
    )
  }

  if (outcome === 'partial') {
    return (
      <>
        {change} will <b className="text-red">change or clear some values</b>. {detail} This cannot be undone.{' '}
      </>
    )
  }

  return (
    <>
      {change} keeps every value. {detail}{' '}
    </>
  )
}

export interface ColumnKindModalProps extends ModalProps {
  newKind: IColumnTypes
  columnId: string | undefined
  onCancel: () => void
}

const ColumnKindModal: React.FC<ColumnKindModalProps> = ({
  id,
  open,
  setOpen,
  newKind,
  columnId,
  onCancel,
  zIndex
}) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { displayErrorMessage } = useApplicationStore()

  const [processing, setProcessing] = useState<boolean>(false)
  const [usedViews, setUsedViews] = useState<ITableViewWithColumns[]>([])

  const column = spreadsheetData.viewDetails.columns.find((col) => col.publicId === columnId)

  const handleClose = () => {
    setOpen(false)
    setProcessing(false)
  }

  const handleFailure = (error: any) => {
    displayErrorMessage(error)
    setProcessing(false)
  }

  const handleUpdateColumn = () => {
    if (columnId) {
      let alterOptions = undefined

      // If the new kind is date or datetime then try to detect the format
      // for alter options
      if (DATE_COLUMN_TYPES.includes(newKind)) {
        if (column) {
          const allValues: string[] = []
          for (const row of spreadsheetData.rows) {
            const value = getRowValue(
              row,
              column.publicId,
              spreadsheetData.columnIdToIndex,
              spreadsheetData.columnStore
            )
            if (value) allValues.push(value as string)
          }

          if (newKind === 'date') {
            alterOptions = { dateConversionFormat: detectLikelyDateFormat(allValues) }
          }

          if (newKind === 'datetime') {
            const likelyFormat = detectLikelyDatetimeFormat(allValues)
            alterOptions = { dateConversionFormat: likelyFormat }
          }
        }
      }

      setProcessing(true)
      updateColumn(
        project.publicId,
        'kind',
        spreadsheetData,
        setSpreadsheetData,
        columnId,
        newKind,
        handleClose,
        handleFailure,
        alterOptions
      )
    }
  }

  useEffect(() => {
    if (open && columnId) {
      if (columnId) {
        api
          .getColumnUsedViews(spreadsheetData.tableDetails.publicId, columnId)
          .then((response) => {
            setUsedViews(response.data)
          })
          .catch((error) => {
            displayErrorMessage(error)
          })
      }
    }
  }, [open])

  const getUsedViewFilters = () => {
    const usedViewFilters = []

    for (let i = 0; i < usedViews.length; i++) {
      let include = false
      const view = usedViews[i]
      for (let j = 0; j < view.filterSettings.length; j++) {
        const filter = view.filterSettings[j]
        if (filter.columnId === columnId) include = true
      }

      if (include) [usedViewFilters.push(view)]
    }

    return usedViewFilters
  }

  const usedViewFilters = getUsedViewFilters()

  return (
    <Modal id={id} open={open} setOpen={setOpen} title="Confirm Column Change" zIndex={zIndex}>
      <div>
        <KindChangeWarning previousKind={column?.kind} newKind={newKind} />
        {usedViewFilters.length > 0 ? (
          <span>
            <br />
            <br />
            Also, <b className="text-red">all filter settings related to this column will be removed</b> from the
            following views in this table:
          </span>
        ) : (
          ''
        )}
      </div>
      {usedViewFilters.length > 0 && (
        <ul className="mt-5 p-5">
          {usedViewFilters.map((view: ITableViewWithColumns, viewNumber: number) => {
            return (
              <li className="mb-[5px]" key={viewNumber}>
                <a
                  href={`${HUB_BASE}${project.publicId}/table/${spreadsheetData.tableDetails.publicId}/view/${view.publicId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {view.name}
                </a>
              </li>
            )
          })}
        </ul>
      )}
      <div className="flex justify-end mt-[30px]">
        <Button
          className="mr-[15px] max-w-[150px]"
          internalType="secondary"
          disabled={processing}
          onClick={() => {
            if (!processing) {
              setOpen(false)
              onCancel()
            }
          }}
        >
          Keep Current Kind
        </Button>
        <Button
          data-tour="confirm-change-column-kind"
          internalType="danger"
          className="max-w-[150px]"
          isLoading={processing}
          onClick={() => {
            if (!processing) handleUpdateColumn()
          }}
        >
          Change Kind
        </Button>
      </div>
    </Modal>
  )
}

export default ColumnKindModal
